import {
  createSlice,
  isFulfilled,
  isPending,
  isRejected,
} from '@reduxjs/toolkit';

import { RootState } from '../..';
import { IUser } from '../../UserSlice';
import { ISubscriptionPlan } from '../SubscriptionPlansSlice';
import { IFlexiblePlan } from '../FlexiblePlansSlice';
import { IInvoice, IInvoiceAdminNote } from '../InvoicesSlice';
import {
  adminCreateTransactionLogAdminNote,
  adminDeleteTransactionLogAdminNote,
  adminGetTransactionLogs,
} from './adminApi';
import { getTransactionLogs } from './api';
import { ICourse } from '../CoursesSlice';

export interface ITransactionLogAdminNote {
  _id: string;
  description: string;
  createdBy: string | IUser;
  createdAt: string;
}

export interface ITransactionLog {
  _id: string;
  description: string;
  user: string | IUser;
  creditChange: number;
  currentBalance: number;
  date: string;
  course?: string | ICourse;
  flexiblePlan?: string | IFlexiblePlan;
  subscriptionPlan?: string | ISubscriptionPlan;
  invoice?: string | IInvoice;
  isCustomPlan?: boolean;
  isPremiumUpgrade?: boolean;
  adminNotes?: IInvoiceAdminNote[];
  updatedBy?: string | IUser;
  totalBalance?: number;
}

export interface IInitialTransactionLogsState {
  status: 'idle' | 'loading' | 'rejected';
  transactionLogListData?: {
    limit?: number;
    offset?: number;
    totalDocs?: number;
    list: Array<ITransactionLog>;
  };
  // ADMIN
  adminTransactionLogs?: {
    limit?: number;
    offset?: number;
    totalDocs?: number;
    list: Array<ITransactionLog>;
  };
}

const initialState: IInitialTransactionLogsState = {
  status: 'idle',
  transactionLogListData: undefined,
  // Admin
  adminTransactionLogs: undefined,
};

export const TransactionLogsSlice = createSlice({
  initialState,
  name: 'transactionLogs',
  reducers: {
    resetTransactionLogListData: (state) => {
      state.transactionLogListData = undefined;
    },
    // Admin
    resetAdminTransactionLogs: (state) => {
      state.adminTransactionLogs = undefined;
    },
  },
  extraReducers(builder) {
    // Admin
    builder
      .addCase(adminGetTransactionLogs.fulfilled, (state, action) => {
        const result = action.payload;

        const newAdminTransactionLogs = {
          limit: result.limit,
          offset: result.offset,
          totalDocs: result.totalDocs || 0,
          list: result.docs || [],
        };

        state.adminTransactionLogs = newAdminTransactionLogs;
      })
      .addCase(
        adminCreateTransactionLogAdminNote.fulfilled,
        (state, action) => {
          const result = action.payload;

          if (state.adminTransactionLogs) {
            const newAdminTransactionLogs = { ...state.adminTransactionLogs };

            const index = newAdminTransactionLogs.list.findIndex(
              (item) => item._id === result._id
            );

            if (index > -1) {
              newAdminTransactionLogs.list[index] = result;
            }

            state.adminTransactionLogs = newAdminTransactionLogs;
          }
        }
      )
      .addCase(
        adminDeleteTransactionLogAdminNote.fulfilled,
        (state, action) => {
          const result = action.payload;

          if (state.adminTransactionLogs) {
            const newAdminTransactionLogs = { ...state.adminTransactionLogs };

            const index = newAdminTransactionLogs.list.findIndex(
              (item) => item._id === result._id
            );

            if (index > -1) {
              newAdminTransactionLogs.list[index] = result;
            }

            state.adminTransactionLogs = newAdminTransactionLogs;
          }
        }
      )
      .addCase(getTransactionLogs.fulfilled, (state, action) => {
        const result = action.payload;

        const newTransactionLogListData = {
          limit: result.limit,
          offset: result.offset,
          totalDocs: result.totalDocs || 0,
          list: result.docs || [],
        };

        state.transactionLogListData = newTransactionLogListData;
      });

    // Common Matchers
    builder.addMatcher(
      isFulfilled(
        getTransactionLogs,
        // ADMIN
        adminGetTransactionLogs,
        adminCreateTransactionLogAdminNote,
        adminDeleteTransactionLogAdminNote
      ),
      (state) => {
        state.status = 'idle';
      }
    );

    builder.addMatcher(
      isRejected(
        getTransactionLogs,
        // ADMIN
        adminGetTransactionLogs,
        adminCreateTransactionLogAdminNote,
        adminDeleteTransactionLogAdminNote
      ),
      (state) => {
        state.status = 'rejected';
      }
    );

    builder.addMatcher(
      isPending(
        getTransactionLogs,
        // ADMIN
        adminGetTransactionLogs,
        adminCreateTransactionLogAdminNote,
        adminDeleteTransactionLogAdminNote
      ),
      (state) => {
        state.status = 'loading';
      }
    );
  },
});

export const getTransactionLogsState = (
  state: RootState
): RootState['transactionLogs'] => state.transactionLogs;

export const {
  resetTransactionLogListData,
  // ADMIN
  resetAdminTransactionLogs,
} = TransactionLogsSlice.actions;

export * from './adminApi';
export default TransactionLogsSlice.reducer;
